import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const services = [
  {
    id: 'ai-ml',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <circle cx="24" cy="24" r="8" fill="none" stroke="#C9A84C" strokeWidth="2.5"/>
        <circle cx="24" cy="8" r="4" fill="#C9A84C" opacity="0.8"/>
        <circle cx="24" cy="40" r="4" fill="#C9A84C" opacity="0.8"/>
        <circle cx="8" cy="24" r="4" fill="#C9A84C" opacity="0.8"/>
        <circle cx="40" cy="24" r="4" fill="#C9A84C" opacity="0.8"/>
        <line x1="24" y1="16" x2="24" y2="12" stroke="#C9A84C" strokeWidth="2"/>
        <line x1="24" y1="32" x2="24" y2="36" stroke="#C9A84C" strokeWidth="2"/>
        <line x1="16" y1="24" x2="12" y2="24" stroke="#C9A84C" strokeWidth="2"/>
        <line x1="32" y1="24" x2="36" y2="24" stroke="#C9A84C" strokeWidth="2"/>
        <circle cx="38" cy="10" r="3" fill="#0B1C4E" opacity="0.6"/>
        <circle cx="10" cy="38" r="3" fill="#0B1C4E" opacity="0.6"/>
        <circle cx="10" cy="10" r="3" fill="#0B1C4E" opacity="0.6"/>
        <circle cx="38" cy="38" r="3" fill="#0B1C4E" opacity="0.6"/>
      </svg>
    ),
    title: 'AI & Machine Learning',
    desc: 'Deep learning models, neural networks, NLP systems, computer vision and advanced AI project implementations.',
    tags: ['TensorFlow', 'PyTorch', 'Keras'],
    color: 'from-amber-50 to-yellow-50',
    border: 'border-amber-200',
  },
  {
    id: 'research-paper',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <rect x="8" y="6" width="32" height="36" rx="3" fill="none" stroke="#0B1C4E" strokeWidth="2.5"/>
        <line x1="14" y1="16" x2="34" y2="16" stroke="#C9A84C" strokeWidth="2.5"/>
        <line x1="14" y1="23" x2="34" y2="23" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="14" y1="29" x2="28" y2="29" stroke="#C9A84C" strokeWidth="1.5"/>
        <circle cx="34" cy="36" r="7" fill="#C9A84C"/>
        <path d="M31 36l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Research Paper Writing',
    desc: 'IEEE/Springer/Elsevier standard research papers, literature reviews, SCI-indexed journal publications.',
    tags: ['IEEE', 'Springer', 'SCI Index'],
    color: 'from-blue-50 to-indigo-50',
    border: 'border-blue-200',
  },
  {
    id: 'iot',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <rect x="18" y="18" width="12" height="12" rx="2" fill="#0B1C4E"/>
        <circle cx="12" cy="12" r="4" fill="none" stroke="#C9A84C" strokeWidth="2"/>
        <circle cx="36" cy="12" r="4" fill="none" stroke="#C9A84C" strokeWidth="2"/>
        <circle cx="12" cy="36" r="4" fill="none" stroke="#C9A84C" strokeWidth="2"/>
        <circle cx="36" cy="36" r="4" fill="none" stroke="#C9A84C" strokeWidth="2"/>
        <line x1="16" y1="12" x2="18" y2="21" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="32" y1="12" x2="30" y2="21" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="16" y1="36" x2="18" y2="27" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="32" y1="36" x2="30" y2="27" stroke="#C9A84C" strokeWidth="1.5"/>
      </svg>
    ),
    title: 'IoT & Embedded Systems',
    desc: 'Arduino, Raspberry Pi, ESP32, smart sensor networks, industrial IoT and home automation systems.',
    tags: ['Arduino', 'ESP32', 'Raspberry Pi'],
    color: 'from-green-50 to-emerald-50',
    border: 'border-green-200',
  },
  {
    id: 'cloud',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <path d="M10 30a8 8 0 01-2-15.7A12 12 0 0132 14a8 8 0 010 16H10z" fill="none" stroke="#0B1C4E" strokeWidth="2.5"/>
        <path d="M22 22v10m0 0l-4-4m4 4l4-4" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Cloud Computing & DevOps',
    desc: 'AWS, Azure, GCP deployments, containerization with Docker/Kubernetes, CI/CD pipelines.',
    tags: ['AWS', 'Docker', 'Kubernetes'],
    color: 'from-sky-50 to-blue-50',
    border: 'border-sky-200',
  },
  {
    id: 'data-science',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <rect x="6" y="36" width="6" height="8" rx="1" fill="#C9A84C"/>
        <rect x="15" y="28" width="6" height="16" rx="1" fill="#0B1C4E"/>
        <rect x="24" y="20" width="6" height="24" rx="1" fill="#C9A84C"/>
        <rect x="33" y="14" width="6" height="30" rx="1" fill="#0B1C4E" opacity="0.7"/>
        <polyline points="9,30 18,22 27,16 36,10" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="36" cy="10" r="3" fill="#C9A84C"/>
      </svg>
    ),
    title: 'Data Science & Analytics',
    desc: 'Big data processing, predictive analytics, data visualization, business intelligence dashboards.',
    tags: ['Python', 'R', 'Tableau'],
    color: 'from-purple-50 to-violet-50',
    border: 'border-purple-200',
  },
  {
    id: 'web-mobile',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <rect x="4" y="8" width="28" height="20" rx="3" fill="none" stroke="#0B1C4E" strokeWidth="2.5"/>
        <rect x="28" y="18" width="16" height="22" rx="3" fill="none" stroke="#C9A84C" strokeWidth="2.5"/>
        <line x1="4" y1="22" x2="32" y2="22" stroke="#C9A84C" strokeWidth="1.5"/>
        <circle cx="36" cy="36" r="2" fill="#C9A84C"/>
      </svg>
    ),
    title: 'Web & Mobile Development',
    desc: 'Full-stack web apps, React/Angular/Vue, Flutter/React Native mobile applications.',
    tags: ['React', 'Flutter', 'Node.js'],
    color: 'from-orange-50 to-amber-50',
    border: 'border-orange-200',
  },
  {
    id: 'blockchain',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <rect x="6" y="20" width="10" height="8" rx="2" fill="#0B1C4E"/>
        <rect x="20" y="12" width="10" height="8" rx="2" fill="#C9A84C"/>
        <rect x="20" y="28" width="10" height="8" rx="2" fill="#C9A84C"/>
        <rect x="34" y="20" width="10" height="8" rx="2" fill="#0B1C4E"/>
        <line x1="16" y1="24" x2="20" y2="16" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="16" y1="24" x2="20" y2="32" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="30" y1="16" x2="34" y2="24" stroke="#C9A84C" strokeWidth="1.5"/>
        <line x1="30" y1="32" x2="34" y2="24" stroke="#C9A84C" strokeWidth="1.5"/>
      </svg>
    ),
    title: 'Blockchain & Web3',
    desc: 'Smart contracts, DeFi platforms, NFT marketplaces, Solidity, Ethereum & Polygon development.',
    tags: ['Solidity', 'Ethereum', 'Web3'],
    color: 'from-slate-50 to-gray-50',
    border: 'border-slate-200',
  },
  {
    id: 'phd-support',
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10">
        <path d="M24 6l4 8h9l-7 5 3 9-9-6-9 6 3-9-7-5h9z" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinejoin="round"/>
        <circle cx="24" cy="36" r="5" fill="#0B1C4E" opacity="0.2" stroke="#0B1C4E" strokeWidth="2"/>
        <line x1="24" y1="41" x2="24" y2="44" stroke="#0B1C4E" strokeWidth="2"/>
      </svg>
    ),
    title: 'PhD Research Support',
    desc: 'Complete PhD dissertation assistance, thesis writing, statistical analysis, research methodology.',
    tags: ['SPSS', 'MATLAB', 'LaTeX'],
    color: 'from-rose-50 to-pink-50',
    border: 'border-rose-200',
  },
];

function ServiceCard({ service, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: (index % 4) * 0.1 }}
      className={`service-card bg-gradient-to-br ${service.color} border ${service.border}`}
      id={`service-card-${service.id}`}
    >
      {/* Glossy top accent */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-royal-gold to-transparent opacity-60 rounded-t-2xl" />

      {/* Icon */}
      <div className="mb-4 w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center group-hover:shadow-gold transition-all duration-300 group-hover:scale-110">
        {service.icon}
      </div>

      {/* Title */}
      <h3 className="font-playfair text-xl font-bold text-royal-navy mb-2 group-hover:text-royal-gold transition-colors duration-300">
        {service.title}
      </h3>

      {/* Description */}
      <p className="font-inter text-sm text-slate-royal/70 leading-relaxed mb-4">
        {service.desc}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {service.tags.map((tag) => (
          <span key={tag} className="text-xs bg-white/80 border border-parchment-dark text-royal-navy/60 px-2.5 py-1 rounded-full font-medium">
            {tag}
          </span>
        ))}
      </div>

      {/* CTA Arrow */}
      <div className="flex items-center gap-1 text-royal-gold text-sm font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
        Learn more
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </div>

      {/* Hover glow overlay */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-royal-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
}

export default function ServicesSection() {
  const { ref: titleRef, inView: titleInView } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section id="services" className="section-pad bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={titleInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="gold-badge mb-6"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
            </svg>
            Our Premium Services
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={titleInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="section-title mb-4"
          >
            Expert Solutions for Every{' '}
            <span className="text-gradient-gold">Academic Need</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={titleInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-subtitle max-w-2xl mx-auto"
          >
            From final year projects to doctoral dissertations — we deliver excellence 
            across every discipline with AI-powered precision and academic integrity.
          </motion.p>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={titleInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="royal-divider w-24 mx-auto mt-6"
          />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
